import { PendingInvitationSchema } from '@common/schemas/user.schema';
import {
  Notifications as Bell,
  Language as LanguageIcon,
  Menu as MenuIcon,
} from '@mui/icons-material';
import AppBar from '@mui/material/AppBar';
import Badge from '@mui/material/Badge';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import { useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { Icons } from '../icons';
import { ThemeModeToggle } from './theme-mode-toggle';
import { UserNav } from './user-nav';

import {
  EXAMPLE_NOVEL_FALLBACK_LANG,
  EXAMPLE_NOVEL_ID_BY_LANG,
  EXAMPLE_NOVEL_OWNER_ID,
} from '@/config/example-novel-config';
import { useNovel } from '@/context/novel-context';
import { useFirebase } from '@/firebase';
import { useUserProfile } from '@/firebase/hooks/use-user-profile';

interface TopHeaderProps {
  onMobileMenuToggle?: () => void;
  showMobileMenu?: boolean;
  showMobileBrand?: boolean;
  showDesktopBrand?: boolean;
  onLogoClick?: () => void;
}

export function TopHeader({
  onMobileMenuToggle,
  showMobileMenu = false,
  showMobileBrand = false,
  showDesktopBrand = false,
  onLogoClick,
}: TopHeaderProps) {
  const { t, i18n } = useTranslation();
  const { backend } = useFirebase();
  const { userData } = useUserProfile();
  const { isExampleNovel, setSelectedNovelId } = useNovel();
  const navigate = useNavigate();

  const [notiAnchorEl, setNotiAnchorEl] = useState<null | HTMLElement>(null);
  const notiOpen = Boolean(notiAnchorEl);

  const [actioningToken, setActioningToken] = useState<string | null>(null);
  const [actioningTokenMode, setActioningTokenMode] = useState<'accept' | 'decline' | null>(null);

  // Validate pending invitations reactively
  const invitations = z.array(PendingInvitationSchema).safeParse(userData?.pendingInvitations).data || [];

  const [langAnchorEl, setLangAnchorEl] = useState<null | HTMLElement>(null);
  const langOpen = Boolean(langAnchorEl);

  const languageOptions = [
    { code: 'en', label: t('language.english') },
    { code: 'es', label: t('language.spanish') },
    { code: 'de', label: t('language.german') },
    { code: 'fr', label: t('language.french') },
    { code: 'it', label: t('language.italian') },
    { code: 'ca', label: t('language.catalan') },
  ];
  const currentLanguage = i18n.language.split('-')[0];

  const handleLanguageChange = (code: string) => {
    i18n.changeLanguage(code);
    setLangAnchorEl(null);

    if (isExampleNovel) {
      const lang = code.split('-')[0];
      const targetNovelId =
        EXAMPLE_NOVEL_ID_BY_LANG[lang] ??
        EXAMPLE_NOVEL_ID_BY_LANG[EXAMPLE_NOVEL_FALLBACK_LANG];
      setSelectedNovelId(`${EXAMPLE_NOVEL_OWNER_ID};${targetNovelId}`);
      navigate({ to: '/' });
    }
  };

  const handleAccept = async (token: string) => {
    setActioningToken(token);
    setActioningTokenMode('accept');
    try {
      await backend.novels.acceptInvitation(token);
      handleNotiClose();
      window.location.href = '/';
    } catch (err) {
      console.error('Failed to accept invitation:', err);
    } finally {
      setActioningToken(null);
      setActioningTokenMode(null);
    }
  };

  const handleDecline = async (token: string) => {
    setActioningToken(token);
    setActioningTokenMode('decline');
    try {
      await backend.novels.declineInvitation(token);
    } catch (err) {
      console.error('Failed to decline invitation:', err);
    } finally {
      setActioningToken(null);
      setActioningTokenMode(null);
    }
  };

  const handleNotiClick = (event: React.MouseEvent<HTMLElement>) => {
    setNotiAnchorEl(event.currentTarget);
  };

  const handleNotiClose = () => {
    setNotiAnchorEl(null);
  };

  return (
    <AppBar
      position="sticky"
      elevation={0}
      sx={{
        bgcolor: 'background.paper',
        borderBottom: '1px solid',
        borderColor: 'divider',
        zIndex: (theme) => theme.zIndex.drawer - 1,
      }}
    >
      <Toolbar sx={{ gap: 2, minHeight: { xs: 56, sm: 64 } }}>
        {/* Mobile menu button */}
        {showMobileMenu && (
          <IconButton
            edge="start"
            onClick={onMobileMenuToggle}
            sx={{ display: { lg: 'none' } }}
          >
            <MenuIcon sx={{ fontSize: 24 }} />
          </IconButton>
        )}

        {(showMobileBrand || showDesktopBrand) && (
          <Box
            onClick={onLogoClick}
            sx={{
              display: {
                xs: showMobileBrand ? 'flex' : 'none',
                lg: showDesktopBrand ? 'flex' : 'none',
              },
              alignItems: 'center',
              gap: 1.25,
              minWidth: 0,
              cursor: onLogoClick ? 'pointer' : 'default',
              userSelect: 'none',
            }}
          >
            <Icons.logo width={36} height={36} />
            <Typography
              variant="h6"
              sx={{ fontWeight: 800, letterSpacing: '-0.02em', whiteSpace: 'nowrap' }}
            >
              {t('marketing.title')}
            </Typography>
          </Box>
        )}

        <Box sx={{ flexGrow: 1 }} />

        {/* Language selector */}
        <IconButton
          size="small"
          onClick={(e) => setLangAnchorEl(e.currentTarget)}
          title={languageOptions.find(({ code }) => code === currentLanguage)?.label || t('language.english')}
          sx={{
            color: 'text.secondary',
            '&:hover': { color: 'primary.main' },
          }}
        >
          <LanguageIcon sx={{ fontSize: 20 }} />
        </IconButton>

        <Menu
          anchorEl={langAnchorEl}
          open={langOpen}
          onClose={() => setLangAnchorEl(null)}
          PaperProps={{
            elevation: 0,
            sx: {
              overflow: 'visible',
              filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.1))',
              mt: 1.5,
              minWidth: 120,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
            },
          }}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        >
          {languageOptions.map(({ code, label }) => (
            <MenuItem
              key={code}
              onClick={() => handleLanguageChange(code)}
              selected={currentLanguage === code}
            >
              {label}
            </MenuItem>
          ))}
        </Menu>

        {/* Notifications */}
        <IconButton
          size="small"
          onClick={handleNotiClick}
          sx={{
            color: 'text.secondary',
            '&:hover': { color: 'primary.main' },
          }}
        >
          <Badge badgeContent={invitations.length} color="primary">
            <Bell sx={{ fontSize: 20 }} />
          </Badge>
        </IconButton>

        <Menu
          anchorEl={notiAnchorEl}
          open={notiOpen}
          onClose={handleNotiClose}
          PaperProps={{
            elevation: 0,
            sx: {
              overflow: 'visible',
              filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.1))',
              mt: 1.5,
              width: 320,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
            },
          }}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        >
          {invitations.length === 0 ? (
            <Box sx={{ px: 2, py: 3, textAlign: 'center' }}>
              <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                {t('header.no_notifications')}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.disabled', mt: 0.5, display: 'block' }}>
                {t('header.no_notifications_desc')}
              </Typography>
            </Box>
          ) : (
            <Box sx={{ py: 1.5, px: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'text.primary' }}>
                {t('header.notifications_title')}
              </Typography>
              <Divider sx={{ mb: 1.5 }} />
              <Stack spacing={1.5} divider={<Divider />}>
                {invitations.map((inv) => {
                  const roleTranslated = inv.role === 'collaborator'
                    ? t('dashboard.settings.collaborator_badge')
                    : t('dashboard.settings.reader_badge');

                  return (
                    <Box key={inv.token}>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main', mb: 0.5 }}>
                        {t('header.invitation_title')}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, lineHeight: 1.4 }}>
                        {t('header.invited_to_join', {
                          title: inv.novelTitle,
                          role: roleTranslated
                        })}
                      </Typography>
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          onClick={() => handleDecline(inv.token)}
                          disabled={actioningToken !== null}
                          sx={{ borderRadius: 1.5, textTransform: 'none', px: 1.5, py: 0.5 }}
                        >
                          {actioningToken === inv.token && actioningTokenMode === 'decline' ? (
                            <CircularProgress size={16} color="error" />
                          ) : (
                            t('header.decline')
                          )}
                        </Button>
                        <Button
                          size="small"
                          variant="contained"
                          color="primary"
                          onClick={() => handleAccept(inv.token)}
                          disabled={actioningToken !== null}
                          sx={{ borderRadius: 1.5, textTransform: 'none', px: 1.5, py: 0.5 }}
                        >
                          {actioningToken === inv.token && actioningTokenMode === 'accept' ? (
                            <CircularProgress size={16} color="inherit" />
                          ) : (
                            t('header.accept')
                          )}
                        </Button>
                      </Stack>
                    </Box>
                  );
                })}
              </Stack>
            </Box>
          )}
        </Menu>

        <ThemeModeToggle placement="header" />

        {/* User Nav */}
        <UserNav />
      </Toolbar>
    </AppBar>
  );
}
